/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  Linking,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

const BookDetailScreen = ({ route, navigation }) => {
  const { book, bookId } = route.params;
  const [loading, setLoading] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Sample book data with fallbacks
  const _priceNum = Number(book?.price);
  const _pdfUrl = book?.pdfUrl || book?.pdf_url || '';
  const _isFree = book?.isFree === true || _priceNum === 0 || book?.pdf_available === true || !!_pdfUrl;
  const bookData = {
    id: book?.id || bookId || '1',
    title: book?.title || 'Engineering Book Title',
    author: book?.author || 'Unknown Author',
    publisher: book?.publisher || 'Engineering Press',
    price: Number.isFinite(_priceNum) ? _priceNum : 0,
    isFree: _isFree,
    amazonLink: book?.amazonLink || book?.amazon_link || '',
    pdfUrl: _pdfUrl,
    pdfAvailable: book?.pdfAvailable || book?.pdf_available || !!_pdfUrl,
    rating: book?.rating || 4.5,
    reviews: book?.reviews || 128,
    pages: book?.pages || 500,
    format: book?.format || 'Hardcover',
    discipline: book?.discipline || 'Engineering',
    color: book?.color || '#0e4e96',
    icon: book?.icon || 'book',
    description: book?.description || 'A comprehensive guide to engineering principles and applications. This book covers fundamental concepts, practical examples, and real-world case studies to help students and professionals master the subject.',
    tableOfContents: book?.tableOfContents || [
      'Chapter 1: Introduction to Engineering',
      'Chapter 2: Fundamental Principles',
      'Chapter 3: Mathematical Foundations',
      'Chapter 4: Design Methodology',
      'Chapter 5: Case Studies',
      'Chapter 6: Advanced Topics',
      'Chapter 7: Future Directions',
    ],
    features: [
      'Comprehensive coverage of core concepts',
      'Real-world case studies and examples',
      'Practice problems with solutions',
      'Updated with latest industry standards',
      'Suitable for students and professionals',
    ],
    reviews: [
      {
        id: 1,
        user: 'John M.',
        rating: 5,
        comment: 'Essential reference for any engineering student. Clear explanations and excellent examples.',
        date: '2 weeks ago'
      },
      {
        id: 2,
        user: 'Sarah T.',
        rating: 4,
        comment: 'Comprehensive but could use more practical examples. Overall very good resource.',
        date: '1 month ago'
      },
      {
        id: 3,
        user: 'Mike R.',
        rating: 5,
        comment: 'Best book in this field. Highly recommended for both beginners and advanced readers.',
        date: '2 months ago'
      }
    ]
  };

  const openExternal = async (url) => {
    try {
      await Linking.openURL(url);
    } catch (e) {
      Alert.alert('Could not open link', 'Please try again later.');
    }
  };

  const handlePurchase = () => {
    if (bookData.isFree) {
      // Free book: open the hosted PDF if we have one, else be honest.
      if (bookData.pdfUrl) {
        navigation.navigate('PDFViewer', { book: bookData });
      } else {
        Alert.alert(
          'Read this book',
          'A readable copy isn\u2019t available in the app yet for this title.',
          [{ text: 'OK' }]
        );
      }
    } else {
      // Paid book: buy on Amazon via affiliate link (opens browser, no in-app charge).
      if (bookData.amazonLink) {
        Alert.alert(
          'Buy on Amazon',
          `Open Amazon to purchase "${bookData.title}"?`,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Open Amazon', onPress: () => openExternal(bookData.amazonLink) },
          ]
        );
      } else {
        Alert.alert(
          'Not available',
          'A purchase link isn\u2019t available for this book yet.',
          [{ text: 'OK' }]
        );
      }
    }
  };


  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    Alert.alert(
      isBookmarked ? 'Removed from Bookmarks' : 'Added to Bookmarks',
      `"${bookData.title}" has been ${isBookmarked ? 'removed from' : 'added to'} your bookmarks.`
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesome5 key={i} name="star" size={14} color="#fdb913" solid />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesome5 key="half" name="star-half-alt" size={14} color="#fdb913" solid />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <FontAwesome5 key={`empty-${i}`} name="star" size={14} color="#e0e0e0" />
      );
    }

    return stars;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>Description</Text>
            <Text style={styles.description}>{bookData.description}</Text>
            
            <Text style={styles.sectionTitle}>Key Features</Text>
            {bookData.features.map((feature, index) => (
              <View key={index} style={styles.featureItem}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#4CAF50" />
                <Text style={styles.featureText}>{feature}</Text>
              </View>
            ))}
          </View>
        );
      
      case 'contents':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>Table of Contents</Text>
            {bookData.tableOfContents.map((chapter, index) => (
              <TouchableOpacity key={index} style={styles.chapterItem}>
                <Text style={styles.chapterText}>{chapter}</Text>
                <Ionicons name="chevron-forward" size={16} color="#64748B" />
              </TouchableOpacity>
            ))}
          </View>
        );
      
      case 'reviews':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>Reviews ({bookData.reviews.length})</Text>
            {bookData.reviews.map((review) => (
              <View key={review.id} style={styles.reviewItem}>
                <View style={styles.reviewHeader}>
                  <Text style={styles.reviewUser}>{review.user}</Text>
                  <Text style={styles.reviewDate}>{review.date}</Text>
                </View>
                <View style={styles.reviewRating}>
                  {renderStars(review.rating)}
                </View>
                <Text style={styles.reviewComment}>{review.comment}</Text>
              </View>
            ))}
          </View>
        );
      
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f0f4f8" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Book Details</Text>
        <TouchableOpacity style={styles.bookmarkButton} onPress={handleBookmark}>
          <Ionicons 
            name={isBookmarked ? "bookmark" : "bookmark-outline"} 
            size={24} 
            color={isBookmarked ? "#fdb913" : "#0e4e96"} 
          />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Book Info Card */}
        <View style={styles.bookInfoCard}>
          <View style={[styles.bookIconContainer, { backgroundColor: bookData.color }]}>
            <MaterialCommunityIcons name={bookData.icon} size={48} color="#ffffff" />
          </View>
          
          <View style={styles.bookDetails}>
            <Text style={styles.bookTitle}>{bookData.title}</Text>
            <Text style={styles.bookAuthor}>by {bookData.author}</Text>
            <Text style={styles.bookPublisher}>{bookData.publisher}</Text>
            
            <View style={styles.ratingContainer}>
              <View style={styles.starsContainer}>
                {renderStars(bookData.rating)}
              </View>
              <Text style={styles.ratingText}>
                {bookData.rating} ({bookData.reviews.length} reviews)
              </Text>
            </View>
            
            <View style={styles.metaInfo}>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="book-open-page-variant" size={16} color="#64748B" />
                <Text style={styles.metaText}>{bookData.pages} pages</Text>
              </View>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="format-list-bulleted" size={16} color="#64748B" />
                <Text style={styles.metaText}>{bookData.format}</Text>
              </View>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="school" size={16} color="#64748B" />
                <Text style={styles.metaText}>{bookData.discipline}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Price and Action */}
        <View style={styles.actionCard}>
          <View style={styles.priceContainer}>
            <Text style={styles.priceLabel}>Price</Text>
            <Text style={styles.priceText}>
              {bookData.isFree ? 'Free' : `£${bookData.price}`}
            </Text>
          </View>
          
          <TouchableOpacity 
            style={[
              styles.purchaseButton,
              bookData.isFree && styles.downloadButton,
              loading && styles.disabledButton
            ]}
            onPress={handlePurchase}
            disabled={loading}
          >
            {loading ? (
              <Text style={styles.buttonText}>Processing...</Text>
            ) : (
              <>
                <MaterialCommunityIcons 
                  name={bookData.isFree ? "book-open-variant" : "cart"} 
                  size={20} 
                  color="#ffffff" 
                />
                <Text style={styles.buttonText}>
                  {bookData.isFree ? 'Read Book' : 'Buy on Amazon'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        {!bookData.isFree && (
          <Text style={styles.amazonDisclosure}>
            As an Amazon Associate, JobEng earns from qualifying purchases.
          </Text>
        )}

        {/* Tabs */}
        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'overview' && styles.activeTab]}
            onPress={() => setActiveTab('overview')}
          >
            <Text style={[styles.tabText, activeTab === 'overview' && styles.activeTabText]}>
              Overview
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'contents' && styles.activeTab]}
            onPress={() => setActiveTab('contents')}
          >
            <Text style={[styles.tabText, activeTab === 'contents' && styles.activeTabText]}>
              Contents
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'reviews' && styles.activeTab]}
            onPress={() => setActiveTab('reviews')}
          >
            <Text style={[styles.tabText, activeTab === 'reviews' && styles.activeTabText]}>
              Reviews
            </Text>
          </TouchableOpacity>
        </View>

        {/* Tab Content */}
        {renderTabContent()}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  bookmarkButton: {
    padding: 5,
  },
  container: {
    flex: 1,
    padding: 15,
  },
  bookInfoCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  bookIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: 16,
  },
  bookDetails: {
    alignItems: 'center',
  },
  bookTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#0e4e96',
    textAlign: 'center',
    marginBottom: 8,
  },
  bookAuthor: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 4,
  },
  bookPublisher: {
    fontSize: 14,
    color: '#94A3B8',
    marginBottom: 12,
  },
  ratingContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  starsContainer: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  ratingText: {
    fontSize: 14,
    color: '#64748B',
  },
  metaInfo: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  actionCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  priceContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  priceLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  priceText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  purchaseButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 10,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  downloadButton: {
    backgroundColor: '#4CAF50',
  },
  disabledButton: {
    backgroundColor: '#94A3B8',
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  amazonDisclosure: {
    fontSize: 11,
    color: '#8E8E93',
    textAlign: 'center',
    paddingHorizontal: 24,
    marginTop: 8,
    marginBottom: 4,
    fontStyle: 'italic',
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 4,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabContent: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 12,
  },
  description: {
    fontSize: 16,
    color: '#64748B',
    lineHeight: 24,
    marginBottom: 20,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
    flex: 1,
  },
  chapterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  chapterText: {
    fontSize: 14,
    color: '#0e4e96',
    flex: 1,
  },
  reviewItem: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  reviewUser: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  reviewDate: {
    fontSize: 12,
    color: '#94A3B8',
  },
  reviewRating: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  reviewComment: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
});

export default BookDetailScreen;
